import React, { useCallback, useEffect } from 'react';
import { toast } from 'react-toastify';
import { useQuery, useMutation, gql } from '@apollo/client';
import Lottie from 'react-lottie-player';

import Table from 'Components/Table';
import { ReactComponent as LightImg } from '../../assets/vacationsHistoryEmptyStateLight.svg';
import { ReactComponent as DarkImg } from '../../assets/vacationsHistoryEmptyStateDark.svg';
import formatDate from 'Shared/util/formatDate';
import EmptyPage from 'Components/EmptyPage';
import animationData from 'Shared/spiner_blue.json';
import ReactGA from 'Shared/analytics';
import withErrorBoundary from 'Components/ErrorBoundary/ErrorBoundary';
import { logger } from 'RootShared/utility/ErrorLogger';
import useDocumentTitle from 'Shared/hooks/useDocumentTitle';

const columns = [
  {
    title: 'DATES',
    cellProps: {
      accessor: 'dates',
      width: 400,
    },
  },
  {
    title: 'DAYS',
    cellProps: {
      accessor: 'days',
      width: 128,
    },
  },
  {
    title: 'REASON',
    cellProps: {
      accessor: 'reason',
      width: 176,
    },
  },
  {
    title: 'ALLOCATION',
    cellProps: {
      accessor: 'allocation',
      width: 186,
    },
  },
  {
    title: '',
    cellProps: {
      accessor: 'cancel',
      width: 120,
    },
  },
  {
    title: 'STATUS',
    cellProps: {
      accessor: 'status',
      width: 148,
    },
  },
];

export const mutationCancelLeaveRequest = gql`
  mutation TabHistoryCancelLeaveRequestMutation($id: Int!) @api(name: root) {
    cancelLeaveRequest(id: $id) {
      result
    }
  }
`;

export const queryGetVacationsData = gql`
  query TabHistoryVacationsQuery @api(name: root) {
    requests {
      id
      dateFrom
      dateTo
      days
      comment
      reason {
        text
      }
      allocation {
        validFrom
        reason {
          text
        }
      }
      status
    }
  }
`;

const TabHistory = () => {
  const { loading, data, error, refetch } = useQuery(queryGetVacationsData);
  useDocumentTitle('Vacations — History');
  const [cancelLeaveRequest] = useMutation(mutationCancelLeaveRequest, {
    onCompleted() {
      refetch();
    },
    onError(error) {
      logger.error(error);
    },
  });

  useEffect(() => {
    refetch();
  }, [refetch]);

  const handleRequestCancel = useCallback(
    (clickedVacationId) => {
      ReactGA.event({
        category: 'Leave request',
        action: 'Cancelation of vacation',
      });

      cancelLeaveRequest({ variables: { id: +clickedVacationId } });
    },
    [cancelLeaveRequest]
  );

  const getTableHistoryData = useCallback(
    (vacations) => {
      return vacations.map((vacation) => ({
        dates: {
          title: `${formatDate(vacation.dateFrom)} - ${formatDate(vacation.dateTo)}`,
        },
        days: {
          title: vacation.days,
        },
        reason: {
          title: vacation.reason.text,
          subtitle: vacation.comment,
        },
        allocation: vacation.allocation
          ? {
              title: `${formatDate(vacation.allocation.validFrom)}`,
              subtitle: vacation.allocation.reason.text,
            }
          : {
              title: 'No allocation',
            },
        cancel: {
          buttonProps:
            vacation.status === 'Requested'
              ? {
                  text: 'Cancel',
                  variant: 'secondary',
                  size: 'small',
                  onClick: () => handleRequestCancel(vacation.id),
                  hasBorder: false,
                }
              : null,
        },
        status: {
          tagProps: {
            text: vacation.status,
            variant: vacation.status === 'Requested' ? 'yellow' : vacation.status === 'Rejected' ? 'red' : 'green',
          },
          buttonProps:
            vacation.status === 'Requested'
              ? {
                  text: 'Cancel',
                  variant: 'secondary',
                  size: 'small',
                  onClick: () => handleRequestCancel(vacation.id),
                  hasBorder: false,
                }
              : null,
        },
      }));
    },
    [handleRequestCancel]
  );

  const getTabHistoryTableComponent = useCallback(
    (data) => {
      const { requests = [] } = data;

      const tableProps = {
        data: getTableHistoryData(requests),
        columns,
        tableSettings: {
          headerHeight: 90,
          paginationHeight: 56,
          rowHeight: 70,
          tablePadding: 32,
          pageSize: window.outerWidth > 767 ? 10 : 3,
        },
      };

      return (
        <>
          {tableProps.data.length ? (
            <div className="tab-vacation-history" data-testid="tab_history">
              <div className="container">
                <Table sortable={false} {...tableProps} />
              </div>
            </div>
          ) : (
            <EmptyPage
              img={document.querySelector("html[data-theme='dark']") ? <DarkImg /> : <LightImg />}
              title={'Blank vacation history'}
              text={
                "Looks like you haven't had a vacation yet.To request your first vacation go to tab Leave Request.  "
              }
            />
          )}
        </>
      );
    },
    [getTableHistoryData]
  );

  if (error) {
    logger.error(error);
    toast(error.message, {
      type: toast.TYPE.ERROR,
      autoClose: 3000,
      closeButton: true,
      hideProgressBar: false,
    });
  }

  return (
    <>
      {loading && (
        <Lottie
          data-testid="loading_history"
          className="lottie"
          loop={true}
          play={true}
          animationData={animationData}
          width={100}
          style={{ width: 100, height: 100, margin: '0px auto' }}
        />
      )}
      {data && getTabHistoryTableComponent(data)}
    </>
  );
};

export default withErrorBoundary(TabHistory);
